import React, {useContext, useMemo} from 'react'
import AppContext from '../appContext'
import {SamplePoint} from './GeoMap'

export interface QCMapSidebarProps {
    samplePoints: SamplePoint[]
    boundaryCoordinates?: Array<{x: number; y: number}>
    hoveredSampleLabel: string | null
    onSampleHover: (label: string) => void
    onSampleLeave: () => void
    selectedSampleLabel?: string | null
    onSampleClick?: (label: string) => void
}

/**
 * Calculate area of a polygon in hectares using the Shoelace formula.
 * Coordinates should be in {x: latitude, y: longitude} format.
 */
function calculateAreaHectares(coordinates: Array<{x: number; y: number}>): number {
    if (coordinates.length < 3) return 0

    // Convert to radians and use spherical excess formula for more accuracy
    const toRad = (deg: number) => deg * Math.PI / 180

    // Use the Shoelace formula with latitude correction
    // This gives approximate area in square degrees, then convert to hectares
    let area = 0
    const n = coordinates.length

    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n
        const coord1 = coordinates[i]
        const coord2 = coordinates[j]
        if (!coord1 || !coord2) continue

        // x = latitude, y = longitude
        const lat1 = coord1.x
        const lon1 = coord1.y
        const lat2 = coord2.x
        const lon2 = coord2.y

        area += toRad(lon2 - lon1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)))
    }

    // Earth radius in meters
    const earthRadius = 6371000
    area = Math.abs(area) * earthRadius * earthRadius / 2

    // Convert square meters to hectares (1 hectare = 10,000 m²)
    return area / 10000
}

const QCMapSidebar: React.FC<QCMapSidebarProps> = ({
    samplePoints,
    boundaryCoordinates,
    hoveredSampleLabel,
    onSampleHover,
    onSampleLeave,
    selectedSampleLabel,
    onSampleClick
}) => {
    const app = useContext(AppContext)

    const subsoilCount = samplePoints.filter(s => s.sampleDepth === 'subsoil').length
    const topsoilCount = samplePoints.length - subsoilCount

    // Sort samples alphabetically by label
    const sortedSamples = useMemo(() =>
        [...samplePoints]
            .filter(p => p.label)
            .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
        [samplePoints]
    )

    // Calculate area from boundary
    const areaHectares = useMemo(() => {
        if (!boundaryCoordinates || boundaryCoordinates.length < 3) return null
        return calculateAreaHectares(boundaryCoordinates)
    }, [boundaryCoordinates])

    return (
        <div className="flex flex-col h-full bg-white border-l border-gray-200 w-48">
            {/* Header section */}
            <div className="p-2 border-b border-gray-100 bg-gray-200">
                <div className="text-xs font-semibold text-gray-700">
                    {app.word('samples')} ({samplePoints.length})
                </div>
                <div className="text-xs text-gray-600 mt-1">
                    <div>{app.word('TopSoil')}: {topsoilCount}</div>
                    <div>{app.word('SubSoil')}: {subsoilCount}</div>
                </div>
                {areaHectares !== null && (
                    <div className="text-xs text-gray-600 mt-1 pt-1 border-t border-gray-300">
                        {app.word('area')}: {areaHectares.toFixed(2)} ha
                    </div>
                )}
            </div>

            {/* Scrollable sample list */}
            <div className="flex-1 overflow-y-auto">
                {sortedSamples.length > 0 ? (
                    <ul>
                        {sortedSamples.map((sample, index) => {
                            const isSelected = sample.label === selectedSampleLabel
                            const isHovered = sample.label === hoveredSampleLabel
                            const isSubsoil = sample.sampleDepth === 'subsoil'

                            return (
                                <li
                                    key={sample.label || index}
                                    className={`px-2 py-1 text-xs cursor-pointer border-b border-gray-100 ${
                                        isSelected
                                            ? 'bg-primary-200 text-gray-900 font-medium'
                                            : isHovered
                                                ? 'bg-primary-100 text-gray-900 font-medium'
                                                : 'text-gray-600 hover:bg-gray-200'
                                    }`}
                                    onClick={() => sample.label && onSampleClick && onSampleClick(sample.label)}
                                    onMouseEnter={() => sample.label && onSampleHover(sample.label)}
                                    onMouseLeave={onSampleLeave}
                                >
                                    <span className={isSubsoil ? 'italic' : ''}>
                                        {sample.label}
                                    </span>
                                    {isSubsoil && (
                                        <span className="ml-1 text-gray-400">(sub)</span>
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                ) : (
                    <div className="p-2 text-xs text-gray-400">
                        {app.word('no_samples')}
                    </div>
                )}
            </div>
        </div>
    )
}

export default QCMapSidebar
